/**
 * TimerSession — walk / run player (entry_mode = "timer").
 *
 * No camera. A big stopwatch with Pause / End. On End we collect a short
 * post-session log (mood + perceived effort 1-5) and POST it to
 * /workouts/timer-session/complete, which marks the session done and maps
 * effort → fatigue_score so it feeds Recovery IQ + the history grid.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Pause, Square, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

const MOODS = [
  { key: 'great', label: 'Great' },
  { key: 'good', label: 'Good' },
  { key: 'ok', label: 'OK' },
  { key: 'tired', label: 'Tired' },
  { key: 'rough', label: 'Rough' },
];

const EFFORTS = [
  { v: 1, label: 'Very easy' },
  { v: 2, label: 'Easy' },
  { v: 3, label: 'Moderate' },
  { v: 4, label: 'Hard' },
  { v: 5, label: 'All out' },
];

const fmt = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

const TimerSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(true);
  const [phase, setPhase] = useState('active'); // active | log
  const [mood, setMood] = useState(null);
  const [effort, setEffort] = useState(null);
  const [saving, setSaving] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.getSession(sessionId)
      .then((r) => setSession(r.data))
      .catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    if (running && phase === 'active') {
      intervalRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running, phase]);

  const endSession = useCallback(() => {
    setRunning(false);
    clearInterval(intervalRef.current);
    setPhase('log');
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/workouts/timer-session/complete', {
        session_id: sessionId,
        duration_seconds: seconds,
        mood,
        perceived_effort: effort,
      });
      window.dispatchEvent(new Event('wellness:refresh'));
      toast.success('Logged. Nice work showing up.');
      navigate('/workouts');
    } catch {
      toast.error('Could not save — try again?');
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto p-6 flex flex-col items-center" data-testid="timer-session">
        <div className="w-full text-center mb-8">
          <p className="text-[11px] uppercase tracking-[0.25em] text-emerald-400 font-secondary font-bold mb-1">
            Walk · Run
          </p>
          <h1 className="font-secondary font-bold text-2xl text-white" data-testid="timer-title">
            {session?.workout_name || "Today's walk"}
          </h1>
        </div>

        {phase === 'active' && (
          <>
            <div
              className="font-secondary font-bold tabular-nums text-white tracking-tight"
              style={{ fontSize: 'clamp(4rem, 18vw, 8rem)', lineHeight: 1 }}
              data-testid="timer-clock"
            >
              {fmt(seconds)}
            </div>
            <div className="flex items-center gap-3 mt-10">
              <button
                type="button"
                onClick={() => setRunning((r) => !r)}
                className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-sm font-secondary font-bold text-sm uppercase tracking-wider"
                data-testid="timer-pause-btn"
              >
                {running ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Resume</>}
              </button>
              <button
                type="button"
                onClick={endSession}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-black hover:bg-primary/90 rounded-sm font-secondary font-bold text-sm uppercase tracking-wider"
                data-testid="timer-end-btn"
              >
                <Square className="w-4 h-4" /> End
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-8 text-center">
              Move at a pace that feels easy to sustain. I'll log it when you're done.
            </p>
          </>
        )}

        {phase === 'log' && (
          <div className="w-full space-y-6" data-testid="timer-log">
            <div className="text-center">
              <p className="text-zinc-400 text-sm">You moved for</p>
              <p className="font-secondary font-bold text-4xl text-primary tabular-nums" data-testid="timer-final-duration">{fmt(seconds)}</p>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">How do you feel?</p>
              <div className="flex flex-wrap gap-2">
                {MOODS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMood(m.key)}
                    className={`px-4 py-2 rounded-sm text-sm border transition-colors ${mood === m.key ? 'border-primary text-primary bg-primary/10' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                    data-testid={`timer-mood-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">How hard was it?</p>
              <div className="grid grid-cols-5 gap-2">
                {EFFORTS.map((e) => (
                  <button
                    key={e.v}
                    onClick={() => setEffort(e.v)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-sm border transition-colors ${effort === e.v ? 'border-primary text-primary bg-primary/10' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                    data-testid={`timer-effort-${e.v}`}
                  >
                    <span className="font-secondary font-bold text-lg">{e.v}</span>
                    <span className="text-[9px] uppercase tracking-wide text-center leading-tight">{e.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving || !mood || !effort}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-black hover:bg-primary/90 rounded-sm font-secondary font-bold text-sm uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="timer-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Log session
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TimerSession;
